import { createReducer } from "typesafe-actions";

import { TerritoryLevel } from "@mapmycustomers/shared";
import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import CountryCode from "@mapmycustomers/shared/enum/CountryCode";

import { Actions as AppActions, initializeApp } from "@app/store/app/actions";

import {
  BoundariesActions,
  ensureDemographicDataLoaded,
  fetchBoundaryStats,
  hideRanges,
  initializeTerritories,
  setBoundaryType,
  setCountryRegions,
  setHighlightedTerritoryId,
  setZoomLevel,
} from "./actions";
import BoundariesLayerData from "./types/BoundariesLayerData";

export const boundariesLayerInitialState: BoundariesLayerData = {
  boundaryStats: {
    [BoundaryType.GEOGRAPHY]: { colors: [], data: [], hiddenRanges: [], ranges: [] },
    [BoundaryType.TERRITORY]: { colors: [], data: [], hiddenRanges: [], ranges: [] },
  },
  boundaryViewStates: {
    [BoundaryType.GEOGRAPHY]: { boundaryLegendId: undefined, level: "state" },
    [BoundaryType.TERRITORY]: { boundaryLegendId: undefined, level: "state" },
  },
  countryRegions: [],
  demographicDataLoading: false,
  demographyDataCountryCode: CountryCode["UNITED STATES OF AMERICA"],
  metadata: {},
  territoriesLoading: false,
  type: BoundaryType.TERRITORY,
  visible: false,
  zoomLevel: TerritoryLevel.ADM2,
};

const boundariesLayer = createReducer<BoundariesLayerData, AppActions | BoundariesActions>(
  boundariesLayerInitialState
)
  .handleAction(initializeApp.success, (state, { payload }) => ({
    ...state,
    ...(payload.metaData?.boundariesSettings ?? payload.metaData?.territoriesSettings ?? {}),
  }))
  .handleAction(initializeTerritories.request, (state) => ({
    ...state,
    territoriesLoading: true,
  }))
  .handleAction(ensureDemographicDataLoaded.request, (state) => ({
    ...state,
    demographicDataLoading: true,
  }))
  .handleAction(ensureDemographicDataLoaded.success, (state, { payload }) => ({
    ...state,
    demographicData: payload.demographicData ?? state.demographicData,
    demographicDataLoading: false,
    demographyDataCountryCode: payload.demographyDataCountryCode ?? state.demographyDataCountryCode,
    metadata: payload.metadata ?? state.metadata,
  }))
  .handleAction(ensureDemographicDataLoaded.failure, (state) => ({
    ...state,
    demographicDataLoading: false,
  }))
  .handleAction([initializeTerritories.success, initializeTerritories.failure], (state) => ({
    ...state,
    territoriesLoading: false,
  }))
  .handleAction(setHighlightedTerritoryId, (state, { payload }) => ({
    ...state,
    highlightedTerritoryId: payload,
  }))
  .handleAction(setBoundaryType, (state, { payload }) => ({
    ...state,
    type: payload,
  }))
  .handleAction(setZoomLevel, (state, { payload }) => ({
    ...state,
    zoomLevel: payload,
  }))
  .handleAction(fetchBoundaryStats.request, (state, { payload }) => ({
    ...state,
    boundaryStats: {
      ...state.boundaryStats,
      [payload.boundaryType]: {
        ...state.boundaryStats[payload.boundaryType],
        // reset hidden ranges when boundaryLegendId or level changes
        hiddenRanges:
          "boundaryLegendId" in payload || "level" in payload || payload.resetRanges
            ? []
            : state.boundaryStats[payload.boundaryType].hiddenRanges,
      },
    },
    boundaryViewStates: {
      ...state.boundaryViewStates,
      [payload.boundaryType]: {
        ...state.boundaryViewStates[payload.boundaryType],
        ...("boundaryLegendId" in payload ? { boundaryLegendId: payload.boundaryLegendId } : {}),
        ...("level" in payload ? { level: payload.level } : {}),
      },
    },
    type: payload.boundaryType,
  }))
  .handleAction(fetchBoundaryStats.success, (state, { payload: { boundaryType, ...rest } }) => ({
    ...state,
    boundaryStats: {
      ...state.boundaryStats,
      [boundaryType]: { ...state.boundaryStats[boundaryType], ...rest },
    },
  }))
  .handleAction(hideRanges, (state, { payload: { boundaryType, hiddenRanges } }) => ({
    ...state,
    boundaryStats: {
      ...state.boundaryStats,
      [boundaryType]: {
        ...state.boundaryStats[boundaryType],
        hiddenRanges,
      },
    },
  }))
  .handleAction(setCountryRegions, (state, { payload }) => ({
    ...state,
    countryRegions: payload,
  }));

export default boundariesLayer;
